"use client"

import { useState, useEffect } from "react"

const TIME_FORMAT_KEY = "aurora-forecast-time-format"

const getStoredTimeFormat = (): "12h" | "24h" => {
    if (typeof window === "undefined") return "12h"
    const stored = localStorage.getItem(TIME_FORMAT_KEY)
    return (stored === "12h" || stored === "24h") ? stored : "12h"
}

export function useTimeFormat() {
    const [timeFormat, setTimeFormat] = useState<"12h" | "24h">("12h")
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        // Load from localStorage on mount (client-side only)
        if (typeof window !== "undefined") {
            const stored = getStoredTimeFormat()
            setTimeFormat(stored)
            setIsLoading(false)

            // Listen for storage changes (e.g., from other tabs or components)
            const handleStorageChange = (e: StorageEvent) => {
                if (e.key !== TIME_FORMAT_KEY) return
                if (e.newValue === null) {
                    setTimeFormat("12h")
                } else if (e.newValue === "12h" || e.newValue === "24h") {
                    setTimeFormat(e.newValue)
                }
            }

            // Listen for custom event (for same-tab updates)
            const handleCustomStorageChange = () => {
                const stored = getStoredTimeFormat()
                setTimeFormat(stored)
            }

            window.addEventListener("storage", handleStorageChange)
            window.addEventListener("timeFormatChanged", handleCustomStorageChange)

            return () => {
                window.removeEventListener("storage", handleStorageChange)
                window.removeEventListener("timeFormatChanged", handleCustomStorageChange)
            }
        }
    }, [])

    const updateTimeFormat = (format: "12h" | "24h") => {
        setTimeFormat(format)
        if (typeof window !== "undefined") {
            try {
                localStorage.setItem(TIME_FORMAT_KEY, format)
                window.dispatchEvent(new Event("timeFormatChanged"))
            } catch (e) {
                console.warn("Failed to persist time format to localStorage", e)
            }
        }
    }

    return {
        timeFormat,
        setTimeFormat: updateTimeFormat,
        isLoading,
    }
}

